import { AfterViewInit, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldGroupBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { provideDayjsDateAdapter } from '@app/shared/extra/dayjs-date-adapter';
import { format } from '@app/shared/extra/decimal-format';
import dayjs, { Dayjs } from 'dayjs';
import { BehaviorSubject, ReplaySubject } from 'rxjs';


@Component({
  selector: 'replacement-value-field-builder',
  templateUrl: './simplified-replacement-value-field-builder.component.html',
  styleUrls: ['./simplified-replacement-value-field-builder.component.scss'],
  providers: [provideDayjsDateAdapter()],
  standalone: false
})
export class SimplifiedReplacementValueFieldBuilderComponent extends AbstractFieldGroupBuilder
  implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;
  public form: FormGroup<{
    replacementValue: FormGroup<{
      simplifiedUnitReplacementValue: FormControl<string>;
      simplifiedReplacementValueDate: FormControl<Dayjs>;
      simplifiedReplacementValue: FormControl<string>;
    }>;
    floorArea: FormControl<string>;
  }>;

  public ngOnInit(): void {
    // Cannot edit this field's group if the user has no write access to some fields in this group
    this.preconditionsForEdition &&= this.fieldGroup.children.every(fieldConfig =>
      this.accessManager.hasFieldWriteAccess(fieldConfig.code, fieldConfig.fieldEntityType)
    );

    this.formStateService.setPath(['simplifiedReplacementValue'], {
      state: new ReplaySubject<FieldStateMode>(1),
      isDisplayed: new BehaviorSubject<boolean>(false),
      isSingleField: true,
      value: {},
      initialValue: {}
    });
    this.initForm();
  }

  /**
   * Checks the current value of a field and determines which state it should be next
   * based on if the computed value can be calculated.
   * @protected
   */
  protected getNextState(): void {
    const isFieldEmpty = (field): boolean => field === void 0 || field === '' || field === null;

    const floorArea = this.form.controls.floorArea.value;
    const simplifiedUnitReplacementValue = this.getFieldGroupValue()['simplifiedUnitReplacementValue'];
    const isEmpty = isFieldEmpty(floorArea) || isFieldEmpty(simplifiedUnitReplacementValue);

    if (isEmpty) {
      this.empty();
    } else {
      this.read();
    }
  }

  /**
   * Initialize form values and validators.
   */
  public initForm(): void {
    const fieldsConfig = this.fieldGroup.children;
    const unitReplacementValueFieldConfig = fieldsConfig.find(fieldConfig => {
      return fieldConfig.fieldCode === 'simplifiedUnitReplacementValue';
    });
    const replacementValueDateFieldConfig = fieldsConfig.find((fieldConfig) => {
      return fieldConfig.fieldCode === 'simplifiedReplacementValueDate';
    });
    const replacementValueFieldConfig = fieldsConfig.find((fieldConfig) => {
      return fieldConfig.fieldCode === 'simplifiedReplacementValue';
    });

    const date = dayjs(this.entity.properties['simplifiedReplacementValueDate'] ?? '');
    this.form = new FormGroup({
      replacementValue: new FormGroup({
        simplifiedUnitReplacementValue: new FormControl(
          this.entity.properties['simplifiedUnitReplacementValue'] ?? '',
          unitReplacementValueFieldConfig.field?.validators.map((validator) => {
            return this.validationService.getValidator(validator, this.entity);
          })
        ),
        simplifiedReplacementValueDate: new FormControl(
          date.isValid() ? date : void 0,
          replacementValueDateFieldConfig.field?.validators.map((validator) => {
            return this.validationService.getValidator(validator, this.entity);
          })
        ),
        simplifiedReplacementValue: new FormControl(
          this.entity.computedProperties['simplifiedReplacementValue'] ?? '',
          replacementValueFieldConfig.field?.validators.map((validator) => {
            return this.validationService.getValidator(validator, this.entity);
          })
        )
      }),
      floorArea: new FormControl({
        value: this.entity.properties['floorArea'] ?? '',
        disabled: true
      })
    });
    const completeReplacementValue = {
      simplifiedUnitReplacementValue: this.entity.properties['simplifiedUnitReplacementValue'],
      simplifiedReplacementValueDate: this.entity.properties['simplifiedReplacementValueDate'],
      simplifiedReplacementValue: this.entity.computedProperties['simplifiedReplacementValue'],
      floorArea: this.entity.properties['floorArea']
    };

    // JSON parse and stringify are used to perform a deep copy of the address
    this.setFieldGroupInitialValue(JSON.parse(JSON.stringify(completeReplacementValue)));
    this.setFieldGroupValue(JSON.parse(JSON.stringify(completeReplacementValue)));
    this.getNextState();
  }

  /**
   * Set up hooks and listen for form state changes.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    this.getFieldGroupState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newMode) => {
        if (newMode === FieldStateMode.AFTER_SAVE) {
          this.refreshEntity();
          // update computed value in form
          this.form.controls.replacementValue.controls.simplifiedReplacementValue.setValue(
            this.entity.computedProperties['simplifiedReplacementValue']?.replaceCommaDecimalSeparator()
          );
          this.setFieldGroupInitialValue(JSON.parse(JSON.stringify(this.getFieldGroupValue())));
          this.getNextState();
        } else {
          this.currentMode = newMode;
        }
      });

    this.fieldGroup.children.forEach((fieldConfig) => {
      if (!fieldConfig.field.computed) {
        // If at least one member of the group is not computed, the group must be displayed
        this.getFieldGroupInfo(['isDisplayed']).next(true);
      }
    });
  }

  /**
   * Prevents typing unwanted characters in a numeric field
   * @param {InputEvent} event
   * @returns {boolean}
   */
  public validateKeypressEvent(event: InputEvent): boolean {
    const input = event.data;
    const pattern = /[0-9\-.,]/;
    return event.inputType != 'insertText' || this.validateInput(input, pattern);
  }

  /**
   * Prevents pasting unwanted characters in a numeric field
   * @param {ClipboardEvent} event
   * @returns {boolean}
   */
  public validatePasteEvent(event: ClipboardEvent): boolean {
    const input = event.clipboardData.getData('text');
    const pattern = /^(?:-?\d+|-?\d{1,3}(?:\s?\d{3})+)?(?:[,.]\d+)?$/;
    return this.validateInput(input, pattern);
  }

  public save(): void {
    const dateValue = this.form.controls.replacementValue.controls.simplifiedReplacementValueDate.value;
    const simplifiedUnitReplacementValue = this.form.controls.replacementValue.controls.simplifiedUnitReplacementValue.value;
    this.setFieldGroupValue({
      simplifiedUnitReplacementValue: simplifiedUnitReplacementValue
        ? format(simplifiedUnitReplacementValue)
        : simplifiedUnitReplacementValue,
      simplifiedReplacementValueDate: dateValue ? dayjs(dateValue).format(this.appConfig.DATE_FORMAT) : ''
    });
    super.save();
  }

  public cancel(): void {
    this.initForm();
  }

  public formatDate(date): string {
    return dayjs(date).format('ll');
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for a certain field for which an error has been raised by the corresponding ValidatorFn.
   * @param field Field's code.
   * @return List of FieldValidators.
   */
  public getErroredValidators(field: string): FieldValidator[] {
    const fieldConfig = this.fieldGroup.children.find(fieldConfig => fieldConfig.code === field);
    return fieldConfig?.field?.validators?.filter(validator => !!this.form.get(field).errors?.[validator.code])
      ?? [];
  }

  private validateInput(input: string, pattern: RegExp): boolean {
    return pattern.test(input);
  }
}
